import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import apiClient from "../service/apiClient";
import { calculateAccreditationRecommendation } from "../utils/accreditationRules";

/**
 * Custom hook for score calculation
 * Handles API calls to calculate scores and recommendation
 *
 * @param {object} programmeData - Programme information
 */
const useCalculation = (programmeData = null) => {
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationResult, setCalculationResult] = useState(null);
  const [calculationError, setCalculationError] = useState(null);

  /**
   * Calculate recommendation based on scores and programme type
   * Uses the accreditation rules engine for consistent logic
   */
  const getRecommendation = useCallback((scores, programmeType = 'UG') => {
    // Use the rules engine instead of hardcoded logic
    const result = calculateAccreditationRecommendation(scores, programmeType);

    return {
      status: result.recommendation.includes('Full') ? 'FULL' :
              result.recommendation.includes('Interim') ? 'INTERIM' : 'DENIED',
      text: result.recommendation,
      description: result.reason,
      details: result.details
    };
  }, []);

  /**
   * Calculate scores and recommendation
   * This can be done client-side or via API call
   */
  const calculateScores = useCallback(
    async (scores, useApi = false) => {
      setIsCalculating(true);
      setCalculationError(null);

      try {
        // Calculate total score
        const totalScore = Object.values(scores).reduce(
          (sum, score) => sum + parseFloat(score || 0),
          0
        );

        // Get programme type
        const programmeType = programmeData?.programmeType || programmeData?.type || 'UG';

        // Get recommendation using rules engine with programme type
        const recommendation = getRecommendation(scores, programmeType);

        // Prepare result
        const result = {
          scores,
          totalScore,
          recommendation,
          calculatedAt: new Date().toISOString(),
          programmeId: programmeData?.id || null,
          programmeName: programmeData?.programmeName || null,
          programmeType,
        };

        // If using API, make the call
        if (useApi) {
          try {
            const response = await apiClient.post("/api/calculate-accreditation", {
              programmeId: programmeData?.id,
              scores,
            });

            // Use API result if available
            if (response.data && response.data.success) {
              result.totalScore = response.data.totalScore || totalScore;
              result.recommendation = response.data.recommendation || recommendation;
              result.apiCalculated = true;
            }
          } catch (apiError) {
            console.warn("API calculation failed, using client-side calculation:", apiError);
            // Continue with client-side calculation
            result.apiCalculated = false;
          }
        } else {
          result.apiCalculated = false;
        }

        setCalculationResult(result);
        setIsCalculating(false);

        return result;
      } catch (error) {
        console.error("Calculation error:", error);
        setCalculationError(error.message || "Failed to calculate scores");
        setIsCalculating(false);
        toast.error("Failed to calculate scores. Please try again.");
        return null;
      }
    },
    [programmeData, getRecommendation]
  );

  /**
   * Validate scores before calculation
   */
  const validateScoresForCalculation = useCallback((scores) => {
    const requiredFields = [
      "academicContent",
      "staffing",
      "physicalFacilities",
      "library",
      "funding",
      "researchCollaboration",
      "tracerEmployer",
    ];

    const missingFields = [];
    const invalidFields = [];

    requiredFields.forEach((field) => {
      const value = scores[field];

      if (value === "" || value === null || value === undefined) {
        missingFields.push(field);
        return;
      }

      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0 || numValue > 100) {
        invalidFields.push(field);
      }
    });

    if (missingFields.length > 0 || invalidFields.length > 0) {
      return {
        valid: false,
        missingFields,
        invalidFields,
        message:
          missingFields.length > 0
            ? `Missing required fields: ${missingFields.join(", ")}`
            : `Invalid values in fields: ${invalidFields.join(", ")}`,
      };
    }

    return {
      valid: true,
      missingFields: [],
      invalidFields: [],
      message: "All scores are valid",
    };
  }, []);

  /**
   * Quick calculate (client-side only)
   */
  const quickCalculate = useCallback(
    (scores) => {
      const validation = validateScoresForCalculation(scores);

      if (!validation.valid) {
        toast.error(validation.message);
        return null;
      }

      return calculateScores(scores, false);
    },
    [calculateScores, validateScoresForCalculation]
  );

  /**
   * Full calculate (with API if available)
   */
  const fullCalculate = useCallback(
    (scores) => {
      const validation = validateScoresForCalculation(scores);

      if (!validation.valid) {
        toast.error(validation.message);
        return null;
      }

      return calculateScores(scores, true);
    },
    [calculateScores, validateScoresForCalculation]
  );

  /**
   * Reset calculation state
   */
  const resetCalculation = useCallback(() => {
    setCalculationResult(null);
    setCalculationError(null);
    setIsCalculating(false);
  }, []);

  return {
    isCalculating,
    calculationResult,
    calculationError,
    calculateScores,
    quickCalculate,
    fullCalculate,
    validateScoresForCalculation,
    getRecommendation,
    resetCalculation,
  };
};

export default useCalculation;